import React from 'react';
import StarRating from '../StarRating/StarRating';
import Banner from '../Banner/Banner';
import SectionPliable from '../SectionPliable/SectionPliable';
import iconFonctionnementAdmin from '../../assets/evaluations/icons/fonctionnement-administratif.svg';
import iconAssistance from '../../assets/evaluations/icons/assistance-informatique.svg';
import iconUtilisationSite from '../../assets/evaluations/icons/utilisation-site.svg';
import iconSatisfaction from '../../assets/evaluations/icons/satisfaction-globale.svg';
import iconDifferenceFin from '../../assets/evaluations/icons/difference-financiere.svg';
import iconTauxRemuneration from '../../assets/evaluations/icons/taux-remuneration.svg';
import iconGainDeTemps from '../../assets/evaluations/icons/gain-de-temps.svg';
import './EvaluationCentralis.css';

// 6 criteres notes 1-5 via menu deroulant (paliers propres). Satisfaction Globale n'est PAS
// un critere : c'est la MOYENNE des cartes ci-dessous (cf. carte dediee, calculee ici).
export type EvalCritere =
  | 'fonctionnement_administratif'
  | 'assistance_informatique'
  | 'utilisation_site'
  | 'difference_financiere'
  | 'taux_remuneration'
  | 'gain_temps';

// Note Globale d'un critere = moyenne + nombre de TOUS les avis Centralis.
export interface EvalGlobale {
  moyenne: number | null;
  nombre: number;
}

// Derniere modification de l'evaluation (date ISO + auteur), pour la banniere verte.
// Champs alignes sur la cle d'API `derniere_maj` (parite avec l'onglet besoins).
export interface EvalLastUpdate {
  date: string | null;
  par: string | null;
}

// Reponse de GET /evaluations/centralis.
// notes = notes partagees de l'organisme (acheteur) / entite (prestataire), pas du user.
// Cote acheteur : 6 criteres. Cote prestataire : 3 criteres communs (les 3 acheteur sont absents).
export interface EvaluationCentralisData {
  cote: 'acheteur' | 'prestataire';
  notes: Partial<Record<EvalCritere, number | null>>;
  // Note Globale par critere (moyenne + nombre, tous avis confondus).
  globales?: Partial<Record<EvalCritere, EvalGlobale>>;
  // Derniere mise a jour de cette evaluation (banniere verte, parite avec l'onglet besoins).
  derniere_maj?: EvalLastUpdate | null;
}

interface EvaluationCentralisProps {
  // Cote derive du role (layout immediat, avant meme l'arrivee des donnees).
  cote: 'acheteur' | 'prestataire';
  data: EvaluationCentralisData | null;
  loading: boolean;
  onRate: (critere: EvalCritere, note: number) => void;
}

// Par defaut, une note non encore posee s'affiche a 5 (formulaire prechargé).
const DEFAULT_NOTE = 5;

// Un palier de menu deroulant : la note (1-5) et son libelle affiche.
interface EvalOption {
  note: number;
  label: string;
}

// Echelle de satisfaction (criteres communs : fonctionnement, assistance, utilisation).
const OPTIONS_SATISFACTION: EvalOption[] = [
  { note: 5, label: '5- Très satisfaisant' },
  { note: 4, label: '4- Satisfaisant' },
  { note: 3, label: '3- Médiocre' },
  { note: 2, label: '2- Difficile' },
  { note: 1, label: '1- Très difficile' },
];
const OPTIONS_DIFFERENCE_FINANCIERE: EvalOption[] = [
  { note: 5, label: '5- Entre 5 et 10% de moins' },
  { note: 4, label: '4- Entre 0 et 5% de moins' },
  { note: 3, label: '3- Entre 0 et 5% de plus' },
  { note: 2, label: '2- Entre 5 et 10% de plus' },
  { note: 1, label: '1- Entre 10 et 15% de plus' },
];
const OPTIONS_TAUX_REMUNERATION: EvalOption[] = [
  { note: 5, label: '5- Entre 0 et 5%' },
  { note: 4, label: '4- Entre 5 et 10%' },
  { note: 3, label: '3- Entre 10 et 15%' },
  { note: 2, label: '2- Entre 15 et 20%' },
  { note: 1, label: '1- Plus de 20%' },
];
const OPTIONS_GAIN_TEMPS: EvalOption[] = [
  { note: 5, label: '5- Supérieur à 6 mois de gagné' },
  { note: 4, label: '4- Entre 3 et 6 mois de gagné' },
  { note: 3, label: '3- Entre 1 et 3 mois de gagné' },
  { note: 2, label: '2- Entre 0 et 30 jours de gagné' },
  { note: 1, label: '1- Entre 0 et 30 jours de plus' },
];

// Les 6 cartes notees. Affichees a l'IDENTIQUE cote acheteur ET prestataire (parite complete :
// le prestataire evalue aussi difference financiere / taux de remuneration / gain de temps).
const CRITERE_CARDS: {
  key: EvalCritere;
  titre: string;
  icon: string;
  options: EvalOption[];
}[] = [
  { key: 'fonctionnement_administratif', titre: 'Fonctionnement administratif proposé', icon: iconFonctionnementAdmin, options: OPTIONS_SATISFACTION },
  { key: 'assistance_informatique', titre: "Qualité de l'assistance informatique", icon: iconAssistance, options: OPTIONS_SATISFACTION },
  { key: 'utilisation_site', titre: "Qualité d'utilisation du site", icon: iconUtilisationSite, options: OPTIONS_SATISFACTION },
  { key: 'difference_financiere', titre: 'Différence financière (coût global)', icon: iconDifferenceFin, options: OPTIONS_DIFFERENCE_FINANCIERE },
  { key: 'taux_remuneration', titre: 'Taux de rémunération', icon: iconTauxRemuneration, options: OPTIONS_TAUX_REMUNERATION },
  { key: 'gain_temps', titre: 'Gain de temps procédural', icon: iconGainDeTemps, options: OPTIONS_GAIN_TEMPS },
];

// "Note Globale 4,20/5" sous les etoiles (agregat tous avis Centralis).
const formatGlobalNote = (g?: EvalGlobale): string =>
  `Note globale ${g && g.moyenne != null ? g.moyenne.toFixed(2).replace('.', ',') : '—'}/5`;

// Bloc agregat : "Note globale X,XX/5" puis "N avis" sur la ligne du dessous.
const GlobaleNote: React.FC<{ g?: EvalGlobale }> = ({ g }) => (
  <span className="eval-card-globale">
    {formatGlobalNote(g)}
    <span className="eval-card-avis">{g?.nombre ?? 0} avis</span>
  </span>
);

// "(4,33/5)" a cote des etoiles. Note saisie -> entier, moyenne satisfaction -> 2 decimales.
const formatNote = (n: number | null, decimales = 0): string =>
  n == null ? '—' : n.toFixed(decimales).replace('.', ',');

// Banniere verte "Derniere mise a jour" (parite avec l'onglet Statistiques et besoins).
// Message permanent : par defaut tant qu'aucune note n'a ete modifiee.
const formatLastUpdate = (m?: EvalLastUpdate | null): string => {
  if (!m?.date) return 'Aucune mise à jour enregistrée pour le moment';
  const d = new Date(m.date);
  const date = d.toLocaleDateString('fr-FR');
  const time = d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  return `Dernière mise à jour le ${date} à ${time} par ${m.par ?? '—'}`;
};

/**
 * Bandeau vert "Derniere mise a jour" de l'evaluation Centralis. Exporte pour etre place
 * a la fois en haut de la section et a la TOUTE FIN de la page (apres la section des avis),
 * dans le profil acheteur comme prestataire.
 */
export const EvaluationLastUpdateBanner: React.FC<{ data: EvaluationCentralisData | null }> = ({ data }) => (
  <Banner variant="success" filled hideIcon message={formatLastUpdate(data?.derniere_maj)} />
);

/**
 * Section commune "Evaluation de Centralis" (onglet "Evaluations"), partagee acheteur + prestataire.
 * Chaque carte : un menu deroulant de paliers (1-5) qui remplit les etoiles + la Note Globale agregee.
 * - Acheteur ET prestataire : 6 cartes (parite complete).
 * - Carte "Satisfaction Globale" : MOYENNE (calculee) des cartes affichees ; non saisissable.
 */
const EvaluationCentralis: React.FC<EvaluationCentralisProps> = ({ data, loading, onRate }) => {
  const cartes = CRITERE_CARDS;

  // Satisfaction Globale = moyenne des notes de l'utilisateur sur les cartes affichees.
  const notesCourantes = cartes.map((c) => data?.notes?.[c.key] ?? DEFAULT_NOTE);
  const moyenneUser = notesCourantes.length
    ? notesCourantes.reduce((a, b) => a + b, 0) / notesCourantes.length
    : null;

  // Note Globale de la satisfaction = moyenne des Notes Globales des cartes affichees
  // (= satisfaction globale de tout Centralis). Nombre d'avis = celui d'un critere commun.
  const moyennesGlobales = cartes
    .map((c) => data?.globales?.[c.key]?.moyenne)
    .filter((m): m is number => m != null);
  const satisfactionGlobale: EvalGlobale = {
    moyenne: moyennesGlobales.length
      ? Math.floor((moyennesGlobales.reduce((a, b) => a + b, 0) / moyennesGlobales.length) * 100) / 100
      : null,
    nombre: data?.globales?.fonctionnement_administratif?.nombre ?? 0,
  };

  return (
    <div className="profil-tab-page evaluations-page">
      {/* Bandeau vert PERMANENT (haut) : derniere modification d'une note Centralis.
          Le bandeau du bas est rendu par la page (toute fin, apres la section des avis). */}
      <EvaluationLastUpdateBanner data={data} />

      <SectionPliable title="Évaluation de Centralis">
        <Banner
          variant="info"
          className="eval-notice"
          message="Vos commentaires concernant Centralis revêtent une importance capitale pour nous. Ils constituent un levier essentiel pour l'amélioration continue de nos services. Vous pouvez modifier vos avis à tout moment."
        />

        <div className="synthese-cards">
          {cartes.map((carte) => {
            const note = data?.notes?.[carte.key] ?? DEFAULT_NOTE;
            return (
              <div key={carte.key} className="synthese-card">
                <img className="synthese-card-icon" src={carte.icon} alt="" aria-hidden="true" />
                <span className="synthese-card-titre">{carte.titre}</span>
                <div className="eval-card-control">
                  {loading ? (
                    <span className="eval-card-select eval-card-select--skeleton" aria-hidden="true" />
                  ) : (
                    <select
                      className="eval-card-select"
                      value={String(note)}
                      onChange={(e) => onRate(carte.key, Number(e.target.value))}
                      aria-label={`Noter : ${carte.titre}`}
                    >
                      {carte.options.map((opt) => (
                        <option key={opt.note} value={opt.note}>{opt.label}</option>
                      ))}
                    </select>
                  )}
                  <span className="eval-card-rating">
                    {loading ? (
                      <StarRating value={null} loading />
                    ) : (
                      <>
                        <StarRating value={note} onRate={(n) => onRate(carte.key, n)} ariaLabel={`Noter : ${carte.titre}`} />
                        <span className="eval-card-note">({formatNote(note)}/5)</span>
                      </>
                    )}
                  </span>
                  {loading ? (
                    <span className="eval-card-globale eval-card-globale--skeleton" aria-hidden="true" />
                  ) : (
                    <GlobaleNote g={data?.globales?.[carte.key]} />
                  )}
                </div>
              </div>
            );
          })}

          {/* Satisfaction Globale : moyenne (calculee) des cartes ci-dessus. Non saisissable. */}
          <div className="synthese-card eval-card--satisfaction">
            <img className="synthese-card-icon" src={iconSatisfaction} alt="" aria-hidden="true" />
            <span className="synthese-card-titre">Satisfaction Globale</span>
            <div className="eval-card-control eval-card-control--satisfaction">
              {/* Note calculee (pas de menu) : la moyenne de l'utilisateur en heros, centree. */}
              <span className="eval-card-rating eval-card-rating--lg">
                {loading ? (
                  <StarRating value={null} loading />
                ) : (
                  <>
                    <StarRating value={moyenneUser} readOnly ariaLabel="Satisfaction globale (moyenne)" />
                    <span className="eval-card-note eval-card-note--lg">{formatNote(moyenneUser, 2)}/5</span>
                  </>
                )}
              </span>
              {loading ? (
                <span className="eval-card-globale eval-card-globale--skeleton" aria-hidden="true" />
              ) : (
                <GlobaleNote g={satisfactionGlobale} />
              )}
            </div>
          </div>
        </div>
      </SectionPliable>
    </div>
  );
};

export default EvaluationCentralis;
